import DataGrid, { Column, Item, Selection } from 'devextreme-react/data-grid';
import { TabPanel } from 'devextreme-react/tab-panel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { InspectionType, InspectionAcceptableFileTypeOptions } from '@apply/constants';
import { formatDate } from '@apply/ui';
import { onFileTypeRender, getSelectableFiles, getUnselectableFiles } from '../functions/functions';
import { onFileRender } from './components/OnFileRender';
import { useInspectionsModalStore, canOrderDrawInspection, canOrderStatusInspection } from '../store/useInspectionsModalStore';
import { useInspectionsStateQuery } from '../queries/inspectionsQueries';

interface NewInspectionFormProps {
    onSubmit: () => void;
    onCancel: () => void;
}

const NewInspectionForm = ({ onSubmit, onCancel }: NewInspectionFormProps) => {
    const { inspectionType, setInspectionType, drawId, setDrawId, fileIds, setFileIds } = useInspectionsModalStore();
    const { data: inspectionsData } = useInspectionsStateQuery();

    const selectableFiles = getSelectableFiles(inspectionsData!.files);
    const unselectableFiles = getUnselectableFiles(inspectionsData!.files);

    return (
        <div
            className='row'
            style={{ border: '1px solid silver', borderRadius: '5px', margin: '0 15px' }}
        >
            <div
                className='columns small-12 large-12'
                style={{ paddingBottom: '3px' }}
            >
                <h2
                    className='decorated'
                    style={{ marginTop: '10px' }}
                >
                    Order New Inspection
                </h2>
                <table>
                    <tbody>
                        <tr style={{ height: '36px' }}>
                            <td style={{ width: '140px' }}>Type:</td>
                            <td>
                                <select
                                    id='selectType'
                                    onChange={(e) => setInspectionType(parseInt(e.target.value) as InspectionType)}
                                    style={{ width: '150px' }}
                                >
                                    <option
                                        value={InspectionType.Draw}
                                        disabled={!canOrderDrawInspection(inspectionsData ?? null)}
                                    >
                                        Draw
                                    </option>
                                    <option
                                        value={InspectionType.Status}
                                        disabled={!canOrderStatusInspection(inspectionsData ?? null)}
                                    >
                                        Status
                                    </option>
                                </select>
                            </td>
                        </tr>
                        {inspectionType === InspectionType.Draw && (
                            <tr>
                                <td>Draw #:</td>
                                <td>
                                    <select
                                        id='drawNumber'
                                        value={drawId ?? ''}
                                        onChange={(e) => setDrawId(e.target.value)}
                                        style={{ width: '150px' }}
                                    >
                                        {inspectionsData!.draws.map((d) => (
                                            <option
                                                key={'draw' + d.id}
                                                value={d.id}
                                                disabled={!canOrderDrawInspection(inspectionsData ?? null, d.id)}
                                            >
                                                {d.number} - {d.drawType}
                                            </option>
                                        ))}
                                    </select>
                                </td>
                            </tr>
                        )}
                        <tr style={{ height: '36px' }}>
                            <td style={{ width: '90px' }}>Borrower:</td>
                            <td>
                                <b>{inspectionsData!.borrowerName}</b>
                            </td>
                        </tr>
                        <tr style={{ height: '36px' }}>
                            <td>Address:</td>
                            <td>
                                <b>
                                    {inspectionsData!.address.street1} {inspectionsData!.address.street2}
                                    {inspectionsData!.address.city}, {inspectionsData!.address.state}{' '}
                                    {inspectionsData!.address.zipCode}
                                </b>
                            </td>
                        </tr>
                        <tr style={{ height: '36px' }}>
                            <td>Requested Date:</td>
                            <td>
                                <b>{formatDate(new Date())}</b>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                Files
                                <span className='helper'>
                                    <span className='tooltip-trigger'>
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </span>
                                    <span className='text'>
                                        Only files with the following file types are able to be submitted with an inspection
                                        request:
                                        {InspectionAcceptableFileTypeOptions &&
                                            InspectionAcceptableFileTypeOptions.map(
                                                (item, index) => (index ? ', ' : '  ') + '.' + item.extension
                                            )}
                                    </span>
                                </span>
                            </td>
                            <td style={{ paddingTop: '20px' }}>
                                <TabPanel>
                                    <Item>
                                        <DataGrid
                                            id='filesGrid'
                                            keyExpr='id'
                                            dataSource={selectableFiles}
                                            showBorders={true}
                                            rowAlternationEnabled={true}
                                            remoteOperations={false}
                                            onSelectionChanged={(e) => setFileIds(e.selectedRowKeys as number[])}
                                            selectedRowKeys={fileIds}
                                            noDataText='No Selectable Plan or Appraisal files found for inclusion in Report Request'
                                        >
                                            <Selection
                                                mode='multiple'
                                                selectAllMode='page'
                                                showCheckBoxesMode='always'
                                            />
                                            <Column
                                                dataField='fileTypeId'
                                                caption='Type'
                                                cellRender={onFileTypeRender}
                                                alignment='left'
                                                width={100}
                                            />
                                            <Column
                                                dataField='fileName'
                                                caption='Name'
                                            />
                                            <Column
                                                dataField='url'
                                                caption='File'
                                                cellRender={onFileRender}
                                                alignment='center'
                                                width={60}
                                            />
                                        </DataGrid>
                                    </Item>
                                    <Item>
                                        <DataGrid
                                            id='unselectableFilesGrid'
                                            keyExpr='id'
                                            dataSource={unselectableFiles}
                                            showBorders={true}
                                            rowAlternationEnabled={true}
                                            remoteOperations={false}
                                            noDataText='No Unselectable Plan or Appraisal files found for inclusion in Report Request'
                                        >
                                            <Column
                                                dataField='fileTypeId'
                                                caption='Type'
                                                cellRender={onFileTypeRender}
                                                alignment='left'
                                                width={100}
                                            />
                                            <Column
                                                dataField='fileName'
                                                caption='Name'
                                            />
                                            <Column
                                                dataField='url'
                                                caption='File'
                                                cellRender={onFileRender}
                                                alignment='center'
                                                width={60}
                                            />
                                        </DataGrid>
                                    </Item>
                                </TabPanel>
                            </td>
                        </tr>
                    </tbody>
                </table>
                <div>
                    <button
                        className='button'
                        type='button'
                        title='Submit Inspection Order'
                        onClick={onSubmit}
                        style={{ padding: '6px' }}
                    >
                        Submit
                    </button>
                    &nbsp;
                    <button
                        className='button hollow'
                        type='button'
                        title='Cancel Inspection Order'
                        onClick={onCancel}
                        style={{ padding: '6px' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewInspectionForm;
